import React, { ReactNode, memo, useMemo, useState } from 'react';

import { useLocation } from 'react-router-dom';

import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Collapse, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';

export interface SidebarSubItemProps {
  label: string;
  url: string;
  selected?: boolean;
  onClick?: () => void;
}

export interface SidebarItemProps {
  label: string;
  icon?: ReactNode;
  url: string;
  subItems?: Array<SidebarSubItemProps>;
  defaultExpanded?: boolean;
}

const ITEM_SX = { py: 0, px: 0 } as const;
const BUTTON_SX = { paddingLeft: 1.5, paddingRight: 0 } as const;
const ICON_SX = { '&.MuiListItemIcon-root': { minWidth: '40px' } } as const;
const SUB_ITEM_BUTTON_SX = { pl: 6, pr: 0 } as const;

const SidebarItem = memo(function SidebarItem({
  label,
  icon,
  url,
  subItems,
  defaultExpanded = false,
}: SidebarItemProps) {
  const { hash } = useLocation();
  const isSelected = useMemo(() => hash === url, [hash, url]);
  const hasSubItems = subItems && subItems.length > 0;
  const [expanded, setExpanded] = useState(defaultExpanded);

  const handleClick = hasSubItems
    ? (e: React.MouseEvent) => {
        e.preventDefault();
        setExpanded((prev) => !prev);
      }
    : undefined;

  return (
    <>
      <ListItem sx={ITEM_SX}>
        <ListItemButton
          component="a"
          href={url}
          selected={!hasSubItems && isSelected}
          onClick={handleClick}
          sx={BUTTON_SX}
        >
          {icon && (
            <ListItemIcon
              sx={{
                ...ICON_SX,
                color: isSelected ? 'primary.main' : 'text.secondary',
              }}
            >
              {icon}
            </ListItemIcon>
          )}
          <ListItemText primary={label} />
          {hasSubItems && (expanded ? <ExpandLess /> : <ExpandMore />)}
        </ListItemButton>
      </ListItem>
      {hasSubItems && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List disablePadding>
            {subItems.map((subItem, index) => {
              const isSubSelected = subItem.selected ?? hash === subItem.url;
              return (
                <ListItem key={`${label}-sub-${index}`} sx={ITEM_SX}>
                  <ListItemButton
                    component="a"
                    href={subItem.url}
                    selected={isSubSelected}
                    onClick={subItem.onClick}
                    sx={SUB_ITEM_BUTTON_SX}
                  >
                    <ListItemText primary={subItem.label} />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Collapse>
      )}
    </>
  );
});

export default SidebarItem;
